import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { Pager } from '../../lib/Pager'
import { SortTh, sortParam, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   ข้อมูลหลัก → ประเภทการลา · backend: src/routes/adminLeaveTypes.js

   ไม่ได้ใช้ MasterTable เพราะสองอย่างที่ MasterTable ไม่รองรับ:
   · ตารางนี้ **ไม่มีคอลัมน์ inactive** — MasterTable ยิง POST /:key/inactive เสมอ
     แต่ที่นี่ลบจริง (มีด่านเช็คของที่อ้างอยู่ก่อน)
   · มี checkbox 5 ช่อง — Field ของ MasterTable เป็น text/select เท่านั้น

   ⚠️ รหัสซ้ำข้ามบริษัททุกตัว ('001' มีทั้ง SM01 SM02 SM07) คีย์ของแถวจึงต้องเป็น
      คู่ (บริษัท + รหัส) ทั้งใน react key และใน URL ที่ยิงกลับ
   ============================================================================ */

const BASE = '/api/admin/leave-types'
const nf = new Intl.NumberFormat('th-TH')

type LeaveType = {
  CompanyCode: string
  LEAVE_TYPE: string
  LEAVE_TYPE_DESC: string
  LEAVE_REMARK: string | null
  MINUTE_CAN_USED: number | string | null
  DEFAULT_MINUTE: number | string | null
  DEFAULT_DAY: number | string | null
  GET_AFTER_DAY: number | null
  CHK_BY_EMP: string | null
  count_calendar_days: boolean
  allow_cross_year: boolean
  once_per_life: boolean
  cap_annual: boolean
}

const keyOf = (r: LeaveType) => `${r.CompanyCode}/${r.LEAVE_TYPE}`
const urlOf = (r: LeaveType) =>
  `${BASE}/${encodeURIComponent(r.CompanyCode)}/${encodeURIComponent(r.LEAVE_TYPE)}`

/** numeric ของ mssql มาเป็น string ผ่าน json — ให้เป็นเลขก่อนจัดรูป */
const num = (v: number | string | null) => (v === null || v === '' ? 0 : Number(v))

export default function LeaveTypes() {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const [rows, setRows] = useState<LeaveType[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sort = useSort([], () => setPage(1))

  const [companyCode, setCompanyCode] = useState('')
  const [companies, setCompanies] = useState<{ code: string; name: string | null }[]>([])

  /** null = ปิด · 'new' = เพิ่ม · แถว = แก้แถวนั้น */
  const [open, setOpen] = useState<LeaveType | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      const sp = sortParam(sort.sort)
      if (sp) qs.set('sort', sp)
      if (companyCode) qs.set('companyCode', companyCode)

      const d = await api<{ rows: LeaveType[]; total: number }>(`${BASE}?${qs}`)
      setRows(d.rows)
      setTotal(d.total)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sort.sort, companyCode])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let alive = true
    api<{ options: { code: string; name: string | null }[] }>('/api/admin/companies/options')
      .then((d) => alive && setCompanies(d.options))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  async function remove(row: LeaveType) {
    if (!window.confirm(`ลบประเภท "${row.LEAVE_TYPE_DESC}" (${row.LEAVE_TYPE}) ?`)) return
    try {
      await api(urlOf(row), { method: 'DELETE' })
      setError(null)
      void load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <>
      <h1>ประเภทการลา</h1>

      {error ? <p className="error">{error}</p> : null}
      {!rows && !error ? <p>กำลังโหลด…</p> : null}

      <div className="emp-toolbar">
        {companies.length > 1 ? (
          <select
            value={companyCode}
            onChange={(e) => {
              setCompanyCode(e.target.value)
              setPage(1)
            }}
            aria-label="บริษัท"
          >
            <option value="">ทุกบริษัท</option>
            {companies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name ? `${c.code} — ${c.name}` : c.code}
              </option>
            ))}
          </select>
        ) : null}

        {mayWrite ? (
          <button type="button" onClick={() => setOpen('new')}>
            เพิ่มประเภทการลา
          </button>
        ) : null}
      </div>

      {rows ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <SortTh label="รหัส" sortKey="code" sort={sort.sort} onToggle={sort.toggle} />
                <SortTh label="ชื่อประเภท" sortKey="name" sort={sort.sort} onToggle={sort.toggle} />
                <SortTh label="บริษัท" sortKey="company" sort={sort.sort} onToggle={sort.toggle} />
                <SortTh label="สิทธิ์ (วัน)" sortKey="days" sort={sort.sort} onToggle={sort.toggle} className="num" />
                <SortTh label="อายุงาน (วัน)" sortKey="after" sort={sort.sort} onToggle={sort.toggle} className="num" />
                <th>เงื่อนไข</th>
                {mayWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={keyOf(r)}>
                  <td className="nowrap">{r.LEAVE_TYPE}</td>
                  <td>{r.LEAVE_TYPE_DESC}</td>
                  <td>{r.CompanyCode}</td>
                  <td className="num">{nf.format(num(r.DEFAULT_DAY))}</td>
                  <td className="num">{nf.format(r.GET_AFTER_DAY ?? 0)}</td>
                  {/* ธงห้าตัวรวมเป็นคำสั้น ๆ — โชว์เป็น 5 คอลัมน์ติ๊กถูกจะกว้างเกินจอ
                      และอ่านยากกว่าเพราะต้องจำว่าคอลัมน์ไหนคืออะไร */}
                  <td>{flagSummary(r) || '—'}</td>
                  {mayWrite ? (
                    <td className="nowrap">
                      <button type="button" className="ghost tiny" onClick={() => setOpen(r)}>
                        แก้ไข
                      </button>{' '}
                      <button type="button" className="ghost tiny" onClick={() => void remove(r)}>
                        ลบ
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {rows && rows.length === 0 ? <p>ยังไม่มีประเภทการลา</p> : null}

      <Pager
        page={page}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onPage={setPage}
        onPageSize={(n) => {
          setPageSize(n)
          setPage(1)
        }}
      />

      {open !== null ? (
        <LeaveTypeDrawer
          row={open === 'new' ? null : open}
          companies={companies}
          mayWrite={mayWrite}
          onClose={() => setOpen(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </>
  )
}

function flagSummary(r: LeaveType) {
  const out: string[] = []
  if (r.CHK_BY_EMP === 'Y') out.push('พนักงานเลือกเอง')
  if (r.count_calendar_days) out.push('นับวันหยุดด้วย')
  if (r.allow_cross_year) out.push('ข้ามปีได้')
  if (r.once_per_life) out.push('ครั้งเดียวตลอดอายุงาน')
  if (r.cap_annual) out.push('จำกัดต่อปี')
  return out.join(' · ')
}

function LeaveTypeDrawer({
  row,
  companies,
  mayWrite,
  onClose,
  onSaved,
}: {
  row: LeaveType | null
  companies: { code: string; name: string | null }[]
  mayWrite: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const creating = row === null

  const [companyCode, setCompanyCode] = useState(row?.CompanyCode ?? companies[0]?.code ?? '')
  const [leaveType, setLeaveType] = useState(row?.LEAVE_TYPE ?? '')
  const [desc, setDesc] = useState(row?.LEAVE_TYPE_DESC ?? '')
  const [remark, setRemark] = useState(row?.LEAVE_REMARK ?? '')
  const [defaultDay, setDefaultDay] = useState(String(num(row?.DEFAULT_DAY ?? 0)))
  const [getAfterDay, setGetAfterDay] = useState(String(row?.GET_AFTER_DAY ?? 0))
  const [minuteCanUsed, setMinuteCanUsed] = useState(String(num(row?.MINUTE_CAN_USED ?? 0)))
  const [defaultMinute, setDefaultMinute] = useState(String(num(row?.DEFAULT_MINUTE ?? 0)))
  const [chkByEmp, setChkByEmp] = useState(row?.CHK_BY_EMP === 'Y')
  const [countCalendarDays, setCountCalendarDays] = useState(row?.count_calendar_days ?? false)
  const [allowCrossYear, setAllowCrossYear] = useState(row?.allow_cross_year ?? false)
  const [oncePerLife, setOncePerLife] = useState(row?.once_per_life ?? false)
  const [capAnnual, setCapAnnual] = useState(row?.cap_annual ?? false)

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const body = JSON.stringify({
        companyCode,
        leaveType,
        leaveTypeDesc: desc,
        leaveRemark: remark,
        defaultDay,
        getAfterDay,
        minuteCanUsed,
        defaultMinute,
        chkByEmp,
        countCalendarDays,
        allowCrossYear,
        oncePerLife,
        capAnnual,
      })
      if (creating) {
        await api(BASE, { method: 'POST', body })
      } else {
        await api(urlOf(row), { method: 'PUT', body })
      }
      setError(null)
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="ปิดแผง" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label="ประเภทการลา">
        <header className="drawer-head">
          <div>
            <p className="drawer-title">{creating ? 'เพิ่มประเภทการลา' : `ประเภท ${row?.LEAVE_TYPE}`}</p>
            <p className="drawer-sub">{creating ? '' : (row?.LEAVE_TYPE_DESC ?? '')}</p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {error ? <p className="error">{error}</p> : null}

          <form className="emp-form" onSubmit={(e) => void save(e)}>
            <div className="field-grid" style={{ gridTemplateColumns: '7rem 9rem minmax(0, 1fr)' }}>
              <label>
                รหัส
                <input
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  maxLength={20}
                  required
                  /* รหัสเป็นคีย์ที่ใบลาเก่าอ้างอยู่ และไม่มี FK ตามให้ —
                     เปลี่ยนแล้วใบลาเก่าจะหาชื่อประเภทไม่เจอ */
                  disabled={!mayWrite || !creating}
                  title={!creating ? 'รหัสเปลี่ยนไม่ได้ — ใบลาเก่าอ้างอยู่' : undefined}
                />
              </label>
              <label>
                บริษัท
                <select
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value)}
                  required
                  disabled={!mayWrite || !creating}
                  title={!creating ? 'ย้ายข้ามบริษัทไม่ได้' : undefined}
                >
                  {companies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                ชื่อประเภท
                <input
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  maxLength={200}
                  required
                  disabled={!mayWrite}
                />
              </label>
            </div>

            <p className="fieldset-title">สิทธิ์</p>
            <div className="field-grid field-grid--even">
              <label>
                สิทธิ์ตั้งต้น (วัน)
                <input
                  value={defaultDay}
                  onChange={(e) => setDefaultDay(e.target.value)}
                  inputMode="numeric"
                  disabled={!mayWrite}
                />
              </label>
              <label>
                ใช้ได้เมื่ออายุงาน (วัน)
                <input
                  value={getAfterDay}
                  onChange={(e) => setGetAfterDay(e.target.value)}
                  inputMode="numeric"
                  disabled={!mayWrite}
                />
              </label>
              <label>
                เพดาน (นาที)
                <input
                  value={minuteCanUsed}
                  onChange={(e) => setMinuteCanUsed(e.target.value)}
                  inputMode="numeric"
                  disabled={!mayWrite}
                />
              </label>
              <label>
                ค่าตั้งต้น (นาที)
                <input
                  value={defaultMinute}
                  onChange={(e) => setDefaultMinute(e.target.value)}
                  inputMode="numeric"
                  disabled={!mayWrite}
                />
              </label>
            </div>
            {/* ของจริง 2880 นาที = 6 วัน คู่กับสิทธิ์ตั้งต้น 6 วัน — ระบบไม่ได้คำนวณ
                ให้ ถ้าตั้งไม่สอดคล้องกันจะได้สองคำตอบจากที่เดียว */}
            <p className="field-note">
              วันละ 480 นาที — สิทธิ์ {nf.format(Number(defaultDay) || 0)} วัน เท่ากับ{' '}
              {nf.format((Number(defaultDay) || 0) * 480)} นาที
            </p>

            <p className="fieldset-title">เงื่อนไข</p>
            <div className="field-grid">
              <label className="check">
                <input
                  type="checkbox"
                  checked={chkByEmp}
                  onChange={(e) => setChkByEmp(e.target.checked)}
                  disabled={!mayWrite}
                />
                พนักงานเลือกเองได้
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={countCalendarDays}
                  onChange={(e) => setCountCalendarDays(e.target.checked)}
                  disabled={!mayWrite}
                />
                นับวันหยุดรวมด้วย
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={allowCrossYear}
                  onChange={(e) => setAllowCrossYear(e.target.checked)}
                  disabled={!mayWrite}
                />
                ลาคร่อมปีได้
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={oncePerLife}
                  onChange={(e) => setOncePerLife(e.target.checked)}
                  disabled={!mayWrite}
                />
                ใช้ได้ครั้งเดียวตลอดอายุงาน
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={capAnnual}
                  onChange={(e) => setCapAnnual(e.target.checked)}
                  disabled={!mayWrite}
                />
                จำกัดต่อปี
              </label>
            </div>

            <div className="field-grid">
              <label className="field-grid--full">
                หมายเหตุ
                <input
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  maxLength={1000}
                  placeholder="เช่น ครั้งละไม่เกิน 15 วัน"
                  disabled={!mayWrite}
                />
              </label>
            </div>

            {mayWrite ? (
              <p>
                <button type="submit" disabled={busy || !leaveType.trim() || !desc.trim()}>
                  บันทึก
                </button>
              </p>
            ) : null}
          </form>
        </div>
      </aside>
    </>
  )
}
